/* @generated */

// ../src/tooltips/control.tsx
import classNames from "classnames";
import * as React from "react";
import {
  useMotion,
  EASE_ENTER,
  EASE_LEAVE
} from "@dropbox/dig-components/motion";
import {
  Overlay
} from "@dropbox/dig-components/overlay";
import { Text } from "@dropbox/dig-components/typography";
import { useTheme } from "@dropbox/dig-foundations";
import { useIsomorphicLayoutEffect } from "@react-hookz/web";

// ../src/tooltips/index.css.ts
import { createRuntimeFn as _7a468 } from "@vanilla-extract/recipes/createRuntimeFn";
var actions = "dig-1i767qdh_21-4-0";
var fullBleed = "dig-1i767qdj_21-4-0";
var illustration = "dig-1i767qdk_21-4-0";
var subtext = "dig-1i767qdi_21-4-0";
var text = _7a468({ defaultClassName: "dig-1i767qdl_21-4-0", variantClassNames: { withMargin: { true: "dig-1i767qdm_21-4-0", false: "dig-1i767qdn_21-4-0" } }, defaultVariants: { withMargin: false }, compoundVariants: [] });
var tooltip = _7a468({ defaultClassName: "dig-1i767qd0_21-4-0", variantClassNames: { shouldDisablePointerEvents: { true: "dig-1i767qd1_21-4-0" } }, defaultVariants: {}, compoundVariants: [] });
var tooltipArrow = "dig-1i767qdo_21-4-0";
var tooltipContent = _7a468({ defaultClassName: "dig-1i767qda_21-4-0", variantClassNames: { variant: { basic: "dig-1i767qdb_21-4-0", rich: "dig-1i767qdc_21-4-0 dig-1i767qd9_21-4-0" }, placement: { up: "dig-1i767qdd_21-4-0", down: "dig-1i767qde_21-4-0", left: "dig-1i767qdf_21-4-0", right: "dig-1i767qdg_21-4-0" } }, defaultVariants: {}, compoundVariants: [] });
var tooltipInner = _7a468({ defaultClassName: "dig-1i767qd2_21-4-0", variantClassNames: { placement: { up: "dig-1i767qd3_21-4-0", down: "dig-1i767qd4_21-4-0", left: "dig-1i767qd5_21-4-0", right: "dig-1i767qd6_21-4-0" }, variant: { basic: "dig-1i767qd7_21-4-0", rich: "dig-1i767qd8_21-4-0" } }, defaultVariants: {}, compoundVariants: [] });

// ../src/tooltips/control.tsx
import { AspectRatio } from "@dropbox/dig-components/aspect_ratio";
var tooltipPlacementTransitionDir = {
  top: "up",
  "top-start": "up",
  "top-end": "up",
  bottom: "down",
  "bottom-start": "down",
  "bottom-end": "down",
  left: "left",
  "left-start": "left",
  "left-end": "left",
  right: "right",
  "right-start": "right",
  "right-end": "right"
};
var getTooltipAnimation = (args) => {
  const { openDelay: openDelayMS, direction } = args;
  const endState = direction === "up" ? `translate(0, -16px)` : direction === "down" ? `translate(0, 16px)` : direction === "left" ? `translate(-16px, 0)` : `translate(16px, 0)`;
  return /* @__PURE__ */ new Map([
    [
      "open",
      {
        keyFrames: {
          transform: endState,
          // This is a bit odd. Let me explain.
          // We want to animate the opacity from 0 to 1 if openDelay is set. This allows it to
          // render hidden and then fade in after a delay. If openDelay is not set, we don't set
          // the beginning keyFrame. This is to allow a Tooltip.Control hardcoded to `open` to not
          // flicker.
          opacity: openDelayMS > 0 ? [0, 1] : 1
        },
        options: {
          duration: 0.125,
          ease: EASE_ENTER,
          // `motion` works in seconds, not ms like most of dig-components
          delay: openDelayMS > 0 ? openDelayMS / 1e3 : 0
        }
      }
    ],
    [
      "close",
      {
        keyFrames: {
          // We do not have a beginning keyFrame to avoid a flicker if you hover quickly over
          // a tooltip with an openDelay.
          opacity: 0
        },
        options: {
          duration: 0.125,
          ease: EASE_LEAVE
        }
      }
    ]
  ]);
};
var RenderAfter = ({ children, overlayRef }) => {
  const [nextTick, setNextTick] = React.useState(false);
  React.useEffect(() => {
    setTimeout(() => {
      setNextTick(true);
    }, 0);
  }, []);
  useIsomorphicLayoutEffect(() => {
    if (nextTick && overlayRef.current) {
      overlayRef.current.update();
    }
  }, [nextTick, overlayRef]);
  return nextTick ? /* @__PURE__ */ React.createElement(React.Fragment, null, children) : null;
};
var Control = React.forwardRef(
  ({
    id,
    className,
    children,
    open,
    boundary,
    placement: _initialPlacement = "bottom",
    variant = "basic",
    triggerRef,
    shouldReturnFocus = true,
    shouldDisablePointerEvents = false,
    inverse = true,
    openDelay = 0,
    maxWidth = 256,
    onDisplayed,
    onHidden,
    contentBreak,
    isPortaled = true,
    offsetDistance,
    auto,
    autoHide = false,
    onChangePlacement,
    _isUnsafelyOverridingTheme = false,
    withIllustration,
    ...rest
  }, ref) => {
    const motionRef = React.useRef(null);
    const [isTriggerRefAvailable, setIsTriggerRefAvailable] = React.useState(false);
    useIsomorphicLayoutEffect(() => {
      var _a;
      if (!isTriggerRefAvailable) {
        setIsTriggerRefAvailable(Boolean(triggerRef.current));
        (_a = overlayRef.current) == null ? void 0 : _a.update();
      }
    }, [isTriggerRefAvailable, triggerRef]);
    const [floatingPlacement, setFloatingPlacement] = React.useState(_initialPlacement);
    const [prevInitialPlacement, setPrevInitialPlacement] = React.useState(_initialPlacement);
    if (_initialPlacement !== prevInitialPlacement) {
      setPrevInitialPlacement(_initialPlacement);
      setFloatingPlacement(_initialPlacement);
    }
    const overlayRef = React.useRef(null);
    const { getInverseProps, getThemeProps, theme } = useTheme();
    const arrowRef = React.useRef(null);
    const inverseModeClassName = _isUnsafelyOverridingTheme === false ? classNames({
      // Since the default of `inverse` is `true`, we apply a `mode` classname regardless
      [getInverseProps().className]: inverse,
      [getThemeProps().className]: !inverse
    }) : void 0;
    const cls = classNames(
      "dig-Tooltip",
      `dig-Tooltip--${variant}`,
      `dig-Tooltip--${floatingPlacement}`,
      tooltip({ shouldDisablePointerEvents }),
      className,
      inverseModeClassName,
      {
        "dig-Tooltip--shouldDisablePointerEvents": shouldDisablePointerEvents
      }
    );
    const contentBreakProperty = contentBreak === "character" ? "break-all" : "normal";
    const styl = {
      maxWidth,
      wordBreak: contentBreakProperty
    };
    const arrowPadding = React.useMemo(() => {
      if (variant === "rich") {
        return 26;
      } else if (floatingPlacement.includes("top") || floatingPlacement.includes("bottom")) {
        return 14;
      } else {
        return 10;
      }
    }, [variant, floatingPlacement]);
    React.useImperativeHandle(ref, () => ({
      update: () => {
        if (overlayRef.current) {
          overlayRef.current.update();
        }
      }
    }));
    const onTransitionComplete = React.useCallback(
      (args) => {
        if (args.currentState === "close") {
          if (typeof onHidden === "function")
            onHidden();
          if (shouldReturnFocus) {
            if (triggerRef && triggerRef.current) {
              if ("focus" in triggerRef.current && typeof triggerRef.current.focus === "function") {
                triggerRef.current.focus();
              }
            }
          }
        } else {
          if (typeof onDisplayed === "function")
            onDisplayed();
        }
      },
      [onHidden, onDisplayed, shouldReturnFocus, triggerRef]
    );
    const hasIllustration = variant === "rich" && withIllustration;
    const direction = tooltipPlacementTransitionDir[floatingPlacement];
    const animationStates = React.useMemo(
      () => getTooltipAnimation({ direction, openDelay }),
      [direction, openDelay]
    );
    const { isMotionRunning } = useMotion({
      elementRef: motionRef,
      states: animationStates,
      currentState: open ? "open" : "close",
      onTransitionComplete
    });
    if (!open && !isMotionRunning) {
      return null;
    }
    return /* @__PURE__ */ React.createElement(
      Overlay,
      {
        id,
        role: "tooltip",
        placement: _initialPlacement,
        boundaryRef: boundary,
        anchorRef: triggerRef,
        className: cls,
        ref: overlayRef,
        style: styl,
        isPortaled,
        arrowRef,
        offsetDistance,
        auto,
        shouldHideOnAnchorHidden: autoHide,
        onChangePlacement: (newPlacement) => {
          setFloatingPlacement(newPlacement);
          if (onChangePlacement)
            onChangePlacement(newPlacement);
        },
        _arrowPadding: theme === "vis2023" ? arrowPadding : void 0,
        ...rest
      },
      /* @__PURE__ */ React.createElement(
        "div",
        {
          className: classNames(
            "dig-Tooltip-inner",
            tooltipInner({
              placement: tooltipPlacementTransitionDir[floatingPlacement],
              variant
            })
          ),
          ref: motionRef
        },
        /* @__PURE__ */ React.createElement(
          "div",
          {
            className: classNames(
              "dig-Tooltip-content",
              tooltipContent({
                variant,
                placement: tooltipPlacementTransitionDir[floatingPlacement]
              })
            ),
            "data-testid": "digTooltipContent"
          },
          hasIllustration && /* @__PURE__ */ React.createElement("div", { className: fullBleed }, /* @__PURE__ */ React.createElement(AspectRatio, { ratio: 1 / 1, maxHeight: 128, className: illustration }, withIllustration)),
          /* @__PURE__ */ React.createElement(Text, { className: "dig-Tooltip-content--text", tagName: "div", size: "small" }, isPortaled && variant === "rich" ? /* @__PURE__ */ React.createElement(RenderAfter, { overlayRef }, children) : children)
        ),
        /* @__PURE__ */ React.createElement("span", { className: classNames("dig-Tooltip-arrow", tooltipArrow), ref: arrowRef })
      )
    );
  }
);
Control.displayName = "Control";

// ../src/tooltips/toggletip.tsx
import * as React2 from "react";

// ../src/_global/constants.ts
var ESC_KEY = "Escape";
var VISUALLY_HIDDEN_CSS = {
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: "1px",
  margin: "0 -1px -1px 0",
  overflow: "hidden",
  padding: "0",
  position: "absolute",
  width: "1px",
  whiteSpace: "nowrap"
};
var buttonDataAttributeName = "data-dig-button";
var buttonDataSelector = `[${buttonDataAttributeName}]`;
var buttonContentDataAttributeName = "data-dig-button-content";
var buttonContentDataSelector = `[${buttonContentDataAttributeName}]`;
var splitButtonDataAttributeName = "data-dig-split-button";
var splitButtonDataSelector = `[${splitButtonDataAttributeName}]`;
var iconButtonDataAttributeName = "data-dig-icon-button";
var iconButtonDataSelector = `[${iconButtonDataAttributeName}]`;

// ../src/tooltips/toggletip.tsx
import { IconButton } from "@dropbox/dig-components/buttons";
import { UIIcon } from "@dropbox/dig-icons";
import { ClickOutside } from "@dropbox/dig-components/click_outside";
import { InfoLine } from "@dropbox/dig-icons/assets";

// ../src/_global/sizes.tsx
var deprecationMap = [
  // [<modern-value>, <legacy-value>]
  ["medium", "standard"]
];
var getMigratedComponentSize = (size) => {
  const mapping = deprecationMap.find(([_newVal, deprecatedVal]) => size === deprecatedVal);
  if (mapping !== void 0) {
    return mapping[0];
  }
  return size;
};

// ../src/tooltips/toggletip.tsx
var Toggletip = React2.forwardRef(
  ({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    className,
    title,
    placement: initialPlacement = "top",
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    auto = true,
    boundary,
    inverse,
    openDelay = 0,
    "aria-label": ariaLabel,
    iconButtonSize: _iconButtonSize = "small",
    src = InfoLine,
    ...rest
  }, ref) => {
    const [open, setOpen] = React2.useState(false);
    const [placement, setPlacement] = React2.useState(initialPlacement);
    const triggerRef = React2.useRef(null);
    const iconButtonSize = getMigratedComponentSize(_iconButtonSize);
    const show = () => setOpen(true);
    const hide = () => {
      setOpen(false);
      setPlacement(initialPlacement);
    };
    const updatePlacement = (placement2) => {
      setPlacement(placement2);
    };
    const handleClick = () => {
      if (open) {
        hide();
      } else {
        show();
      }
    };
    const handleClickOutside = () => {
      hide();
    };
    const handleKeyPress = (evt) => {
      if (evt.key === ESC_KEY) {
        hide();
      }
    };
    const iconButtonProps = {
      "aria-label": ariaLabel,
      onClick: handleClick,
      onKeyDown: handleKeyPress,
      inverse
    };
    return /* @__PURE__ */ React2.createElement(React2.Fragment, null, /* @__PURE__ */ React2.createElement(
      ClickOutside,
      {
        className: "dig-Toggletip-backdrop",
        isActive: open,
        onClickOutside: handleClickOutside,
        "data-testid": "digToggletipBackdrop"
      },
      /* @__PURE__ */ React2.createElement(
        IconButton,
        {
          variant: "transparent",
          ref: triggerRef,
          ...iconButtonProps,
          size: iconButtonSize,
          type: "button"
        },
        /* @__PURE__ */ React2.createElement(UIIcon, { src, size: iconButtonSize })
      ),
      /* @__PURE__ */ React2.createElement(
        Control,
        {
          triggerRef,
          open,
          auto: true,
          placement,
          onChangePlacement: updatePlacement,
          boundary,
          shouldReturnFocus: false,
          openDelay,
          ref,
          ...rest
        },
        title
      )
    ), /* @__PURE__ */ React2.createElement("div", { role: "log", "aria-live": "assertive", "aria-relevant": "additions", style: VISUALLY_HIDDEN_CSS }, open && title));
  }
);
Toggletip.displayName = "Toggletip";

// ../src/tooltips/tooltip.tsx
import * as React7 from "react";
import { useUniqueId } from "@dropbox/dig-components/hooks";

// ../src/tooltips/title.tsx
import * as React3 from "react";
import { Title as TypographyTitle } from "@dropbox/dig-components/typography";
var Title = ({
  children,
  tagName = "div",
  color = "inherit",
  size = "medium",
  ...rest
}) => {
  return /* @__PURE__ */ React3.createElement(TypographyTitle, { ...rest, color, size, tagName }, children);
};

// ../src/tooltips/text.tsx
import * as React4 from "react";
import cx from "classnames";
import { Text as TypographyText } from "@dropbox/dig-components/typography";
var Text2 = ({
  children,
  className = "",
  color = "inherit",
  size = "medium",
  tagName = "p",
  withMargin = true,
  ...rest
}) => {
  const cls = cx(text({ withMargin }), className);
  return /* @__PURE__ */ React4.createElement(
    TypographyText,
    {
      ...rest,
      color,
      size,
      tagName,
      className: cls,
      _withMargins: false
    },
    children
  );
};

// ../src/tooltips/actions.tsx
import * as React5 from "react";
import cx2 from "classnames";
var Actions = ({
  children,
  className = "",
  ...rest
}) => {
  const cls = cx2(actions, className);
  return /* @__PURE__ */ React5.createElement("div", { className: cls, ...rest }, children);
};

// ../src/tooltips/subtext.tsx
import * as React6 from "react";
import cx3 from "classnames";
import { Text as Text3 } from "@dropbox/dig-components/typography";
var Subtext = ({
  children,
  className = "",
  ...rest
}) => {
  const cls = cx3(subtext, className);
  return /* @__PURE__ */ React6.createElement(Text3, { tagName: "span", color: "subtle", className: cls, ...rest }, children);
};

// ../src/tooltips/tooltip.tsx
var mergeEventProps = (children, props) => {
  const merged = {};
  for (const [eventName, handler] of Object.entries(props)) {
    merged[eventName] = (evt) => {
      handler(evt);
      if (React7.isValidElement(children) && children.props[eventName])
        children.props[eventName](evt);
    };
  }
  return merged;
};
var Tooltip = React7.forwardRef(
  ({
    id,
    children,
    title,
    placement: initialPlacement = "top",
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    auto = true,
    boundary,
    shouldCloseOnClick = false,
    openDelay = 0,
    maxWidth,
    triggerRef: reusedTriggerRef,
    inverse = true,
    contentBreak,
    offsetDistance,
    autoHide = false,
    onDisplayed,
    ...rest
  }, ref) => {
    const tooltipId = useUniqueId({ prefix: "dig-Tooltip-", idOverride: id });
    const [open, setOpen] = React7.useState(false);
    const [displayed, setDisplayed] = React7.useState(false);
    const [placement, setPlacement] = React7.useState(initialPlacement);
    const triggerRef = reusedTriggerRef || React7.useRef(null);
    const controlRef = React7.useRef(null);
    const show = () => setOpen(true);
    const hide = React7.useCallback(() => {
      setOpen(false);
      setDisplayed(false);
      setPlacement(initialPlacement);
    }, [initialPlacement]);
    const handleDisplayed = () => {
      if (onDisplayed)
        onDisplayed();
      setDisplayed(true);
    };
    const updatePlacement = (placement2) => {
      setPlacement(placement2);
    };
    const handleClick = () => {
      if (shouldCloseOnClick)
        hide();
    };
    const handleKeyPress = React7.useCallback(
      (evt) => {
        if (evt.key === ESC_KEY) {
          hide();
        }
      },
      [hide]
    );
    React7.useEffect(() => {
      if (open)
        document.addEventListener("keydown", handleKeyPress);
      else
        document.removeEventListener("keydown", handleKeyPress);
      return () => document.removeEventListener("keydown", handleKeyPress);
    }, [handleKeyPress, open]);
    const childrenProps = {
      /**
       * Using `aria-describedby` over `aria-labelledby` since the tooltip content is an additional
       * hint on top of the trigger's own content.
       *
       * Note that it's only set when tooltip is actually displayed, since AT
       * requires the referenced element (i.e. tooltip contents) to present in
       * the DOM to work.
       *
       * Example: AT will first read the Button, then the aria-describedby as a hint.
       */
      "aria-describedby": open && displayed ? tooltipId : void 0,
      // Since we're cloning these event props into the `children`, ensure they don't get swallowed
      // since there exists internal handlers that manages the showing/hiding of the tooltips.
      ...mergeEventProps(children, {
        onMouseEnter: show,
        onMouseLeave: hide,
        onFocus: show,
        onBlur: hide,
        onClick: handleClick
      })
    };
    React7.useImperativeHandle(ref, () => ({
      update: () => {
        if (controlRef.current) {
          controlRef.current.update();
        }
      }
    }));
    React7.useEffect(() => {
      if (controlRef && controlRef.current)
        controlRef.current.update();
    }, [title]);
    return /* @__PURE__ */ React7.createElement(
      React7.Fragment,
      null,
      /**
       * We need to extend the passed in Buttons, Controls, and any other interactive element
       * here since `aria-describedby` does not work with basic generic roles (i.e: span, div
       * without an explicit interactive role such as "button", "link", etc.)
       */
      React7.isValidElement(children) && React7.cloneElement(children, {
        ref: triggerRef,
        ...childrenProps
      }),
      /* @__PURE__ */ React7.createElement(
        Control,
        {
          triggerRef,
          open,
          auto: true,
          autoHide,
          maxWidth,
          placement,
          onChangePlacement: updatePlacement,
          boundary,
          id: tooltipId,
          shouldDisablePointerEvents: true,
          shouldReturnFocus: false,
          openDelay,
          onDisplayed: handleDisplayed,
          ref: controlRef,
          inverse,
          contentBreak,
          offsetDistance,
          ...rest
        },
        title
      )
    );
  }
);
Tooltip.displayName = "Tooltip";
Tooltip.Control = Control;
Tooltip.Title = Title;
Tooltip.Text = Text2;
Tooltip.Actions = Actions;
Tooltip.Subtext = Subtext;
export {
  Control,
  Toggletip,
  Tooltip
};
